import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { cx } from "../cx.ts";
import type { ButtonVariant } from "./Button.tsx";

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Accessible name; also the tooltip. Required because the button has no visible text. */
  label: string;
  icon: ReactNode;
  variant?: Exclude<ButtonVariant, "accent">;
  ref?: Ref<HTMLButtonElement>;
}

const VARIANTS: Record<NonNullable<IconButtonProps["variant"]>, string> = {
  primary: "bg-brand text-on-brand hover:brightness-95 sun:bg-black sun:text-white",
  secondary: "border border-line-strong bg-surface text-ink hover:bg-surface-muted",
  ghost: "text-ink hover:bg-surface-muted",
  danger: "text-danger hover:bg-danger-soft",
};

/** A square, icon-only button sized to the theme's minimum target. */
export function IconButton({
  label,
  icon,
  variant = "ghost",
  className,
  type = "button",
  ref,
  ...rest
}: IconButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        "inline-flex size-target shrink-0 items-center justify-center rounded-lg transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50 sun:border-2 sun:border-black",
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {icon}
    </button>
  );
}
